import { useCallback, useState } from "react";
import { FlightAlertsService } from "../../../api/flightAlerts.service";
import { getDashErrorCode } from "./apiError.helper";

export interface OtpOutcome {
  success: boolean;
  errorCode?: string;
}

export interface UseOtpSendResult {
  sendOtp: (phoneNumber: string) => Promise<OtpOutcome>;
  isSending: boolean;
}

const RECAPTCHA_ACTION_SEND_OTP = "send_otp";

/**
 * Mints a fresh reCAPTCHA token right before /otp/send — must match the backend's expected
 * action exactly (AuthV2Service.RECAPTCHA_ACTION_SEND_OTP = "send_otp") or score-mode
 * verification throws an action-mismatch error.
 */
export const useOtpSend = (getToken: (action: string) => Promise<string>): UseOtpSendResult => {
  const [isSending, setIsSending] = useState(false);

  const sendOtp = useCallback(
    async (phoneNumber: string): Promise<OtpOutcome> => {
      setIsSending(true);
      try {
        const recaptchaToken = await getToken(RECAPTCHA_ACTION_SEND_OTP);
        const service = new FlightAlertsService();
        await service.sendOtp(phoneNumber, recaptchaToken);
        return { success: true };
      } catch (err) {
        const errorCode = getDashErrorCode(err);
        return errorCode !== undefined ? { success: false, errorCode } : { success: false };
      } finally {
        setIsSending(false);
      }
    },
    [getToken],
  );

  return { sendOtp, isSending };
};
